/**
 * Account-scoped application access for the Connect shell.
 *
 * Master returns the union of:
 *   1. active `account_applications` rows (name == AppId), and
 *   2. apps allowed by permission key `connect.app.<appId>` (or `*` /
 *      platform.admin via the permission resolver).
 *
 * Shell apps (erp, designer, …) match by application `name` /
 * AppId. Module connectivity (`useModuleAvailability`) is a separate
 * gate: entitled + module online ⇒ openable.
 */

import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { buildAuthHeaders, getApiOrigin, hasAuthCredentials } from '@/auth';
import type { AppId } from '@/integration';

export interface AccountApplicationItem {
  name: string;
  uuid?: string;
  url?: string;
  active: boolean;
  maintenance?: boolean;
}

const REFRESH_INTERVAL_MS = 30_000;

const granted = ref<Map<string, AccountApplicationItem>>(new Map());
/** True after any fetch attempt finished (success or fail) so UI is not stuck. */
const loaded = ref(false);
/**
 * True only after a successful applications fetch. Entitlement checks fail-open
 * until then so a transient network/HTTP error does not lock legitimate users
 * out with an empty grant map.
 */
const authoritative = ref(false);
let timer: ReturnType<typeof setInterval> | null = null;
let refcount = 0;
let inflight: Promise<void> | null = null;

function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Drop the in-memory grant snapshot (e.g. credential / account switch).
 * Next entitlement checks fail-open until a successful fetch.
 */
export function resetAccountApplications(): void {
  granted.value = new Map();
  loaded.value = false;
  authoritative.value = false;
  inflight = null;
}

async function fetchOnce(): Promise<void> {
  if (inflight) return inflight;
  if (!hasAuthCredentials()) {
    granted.value = new Map();
    loaded.value = true;
    // No credentials ⇒ definitive empty grant set (logged-out).
    authoritative.value = true;
    return;
  }
  inflight = (async () => {
    try {
      const res = await fetch(`${getApiOrigin()}/v1/account/applications`, {
        headers: buildAuthHeaders({ Accept: 'application/json' }),
      });
      if (!res.ok) {
        // Keep previous grants; do not mark authoritative if we never
        // succeeded — fail-open for first-paint / transient 5xx.
        loaded.value = true;
        return;
      }
      const data = (await res.json()) as { applications?: AccountApplicationItem[] };
      const next = new Map<string, AccountApplicationItem>();
      for (const item of data.applications ?? []) {
        if (!item?.name) continue;
        next.set(normalizeName(item.name), {
          ...item,
          name: normalizeName(item.name),
          active: item.active !== false,
        });
      }
      granted.value = next;
      loaded.value = true;
      authoritative.value = true;
    } catch {
      // Keep previous snapshot; fail-open until a successful fetch.
      loaded.value = true;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Shell-level keepalive: App.vue holds this so grants survive route changes. */
let shellHold = false;

function start() {
  if (refcount === 0 && !shellHold) {
    void fetchOnce();
    timer = setInterval(() => {
      void fetchOnce();
    }, REFRESH_INTERVAL_MS);
  } else if (refcount === 0 && shellHold && !timer) {
    // Shell already holds the poller.
  }
  refcount += 1;
}

function stop() {
  refcount = Math.max(0, refcount - 1);
  if (refcount === 0 && !shellHold && timer) {
    clearInterval(timer);
    timer = null;
  }
}

/**
 * Keep grants loaded for the whole shell session (not tied to Home/Rail
 * mount). Prevents first-click races: leave home → unmount → stop poll →
 * navigate with half-loaded entitlement state.
 */
export function holdAccountApplications(): void {
  if (shellHold) return;
  shellHold = true;
  if (!timer) {
    void fetchOnce();
    timer = setInterval(() => {
      void fetchOnce();
    }, REFRESH_INTERVAL_MS);
  } else {
    void fetchOnce();
  }
}

export function releaseAccountApplications(): void {
  shellHold = false;
  if (refcount === 0 && timer) {
    clearInterval(timer);
    timer = null;
  }
}

/**
 * Whether the current account may see / open this shell app.
 * - `admin` is also a normal application grant.
 * - Until the first successful fetch, optimistically allow (same pattern as
 *   module availability) so first paint does not flash everything off.
 */
export function useAccountApplications() {
  onMounted(start);
  onBeforeUnmount(stop);

  const isEntitled = (appId: AppId | string): boolean => {
    const key = normalizeName(String(appId));
    // Fail-open until we have a successful snapshot.
    if (!authoritative.value) return true;
    const item = granted.value.get(key);
    return !!item && item.active;
  };

  return {
    loaded: computed(() => loaded.value),
    /**
     * True once a grant fetch actually SUCCEEDED. `loaded` only says an attempt
     * finished — it is set on a 5xx and on a network error too, where the grant
     * map stays empty while `isEntitled` deliberately fails open. Anything that
     * draws a conclusion from an empty grant set has to read this one.
     */
    authoritative: computed(() => authoritative.value),
    /** Active application names granted to the account. */
    grantedNames: computed(() =>
      [...granted.value.entries()].filter(([, v]) => v.active).map(([k]) => k),
    ),
    isEntitled,
    refresh: fetchOnce,
  };
}

/** Non-hook check for router guards (uses current snapshot). */
export function isAppEntitled(appId: string): boolean {
  if (!authoritative.value) return true;
  const item = granted.value.get(normalizeName(appId));
  return !!item && item.active;
}

export function accountApplicationsLoaded(): boolean {
  return loaded.value;
}

/** True after a successful grants fetch (or definitive logged-out empty set). */
export function accountApplicationsAuthoritative(): boolean {
  return authoritative.value;
}

/** Kick a fetch without mounting a component (login → home). */
export function refreshAccountApplications(): Promise<void> {
  return fetchOnce();
}
